import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { InputStyled } from '@components/dashboard/SharedStyles';
import Colors from '../Colors';
import UnitSwapToggle from './UnitSwapToggle';

interface Props {
  id: string;
  initialValue?: string | number | null;
}

const WeightInput: React.FC<Props> = ({ id, initialValue }) => {
  const { setFieldValue } = useFormikContext();
  const units = ['kg', 'lbs'];
  const [currentUnit, setCurrentUnit] = useState(units[0]);
  const defaultValues = { kg: undefined, lbs: undefined };
  const [unitValues, setUnitValues] = useState(defaultValues);

  useEffect(() => {
    if (!unitValues) return;
    if (currentUnit === 'kg' && unitValues.kg) {
      setFieldValue(id, unitValues.kg);
    } else if (currentUnit === 'lbs' && unitValues.lbs) {
      const kgTotal = unitValues.lbs * 0.453592;
      const roundedValue = Math.round((kgTotal + Number.EPSILON) * 100) / 100;
      if (!Number.isNaN(roundedValue)) setFieldValue(id, roundedValue);
    }
  }, [unitValues]);

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    const numberValue = Number(value);
    const newValues = { ...unitValues, [name as string]: numberValue };
    setUnitValues(newValues);
  };

  const handleUnitSwap = (unit: string) => {
    setFieldValue(id, 0);
    setUnitValues(defaultValues);
    setCurrentUnit(unit);
  };

  const renderCurrentUnit = () => (
    <InputWrapper>
      <InputStyled
        type="number"
        placeholder={currentUnit}
        name={currentUnit}
        value={unitValues[currentUnit]?.toString() ?? initialValue?.toString() ?? ''}
        onChange={onChangeHandler}
      />{' '}
      <UnitLabel>{currentUnit}</UnitLabel>
    </InputWrapper>
  );

  return (
    <Container>
      <UnitSwapToggle units={units} currentUnit={currentUnit} setCurrentUnit={handleUnitSwap} />
      {renderCurrentUnit()}
    </Container>
  );
};

export default WeightInput;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputWrapper = styled.div`
  width: 151px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  gap: 4px;

  & > input {
    text-align: center;
  }
`;

const UnitLabel = styled.div`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: ${Colors.navyBlue};
`;
